import cn from 'classnames';
import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router';
import { CSSTransition } from 'react-transition-group';

import Button from 'components/common/Button';
import Text from 'components/common/Text';
import ToggleButton from 'components/common/ToggleButton';
import Almond from 'components/icons/illustrative/Almond';
import Cow from 'components/icons/illustrative/Cow';
import Chicken from 'components/icons/illustrative/Chicken';
import Fish from 'components/icons/illustrative/Fish';
import Mushroom from 'components/icons/illustrative/Mushroom';
import Peanut from 'components/icons/illustrative/Peanut';
import Pig from 'components/icons/illustrative/Pig';
import Sesame from 'components/icons/illustrative/Sesame';
import Shrimp from 'components/icons/illustrative/Shrimp';
import CaretDown from 'components/icons/navigation/CaretDown';
import { ProgressBarContext, UserContext } from 'components/signUpFunnel/Contexts';
import FunnelHeader from 'components/signUpFunnel/shared/FunnelHeader';
import { BrandContext } from 'context/BrandContext';
import { brandData } from 'helpers/BrandData';
import { getBrandRoute } from 'helpers/routes.js';
import useAnalyticsCallback from 'hooks/useAnalyticsCallback';
import useAnalyticsEffect from 'hooks/useAnalyticsEffect';
import useFunnelMealPlan from 'hooks/useFunnelMealPlan';
import useMetadata from 'hooks/useMetadata';
import { FAMILY, VEGETARIAN, PESCATARIAN, Statuses } from 'lib/constants';
import * as ErrorLogging from 'services/ErrorLogging';
import {
  getPreferences,
  updatePreferences,
  createEvents,
} from 'services/RequestService/endpoints/signUp';
import { updateStatus } from 'services/RequestService/endpoints/user';
import styles from 'styles/components/signUpFunnel/preferences/PreferencesContainer';

import MenuGallery from './MenuGallery';
import PreferencesFormSkeleton from './PreferencesFormSkeleton';

const IconMap = {
  beef: Cow,
  pork: Pig,
  poultry: Chicken,
  shellfish: Shrimp,
  peanuts: Peanut,
  'tree nuts': Almond,
  sesame: Sesame,
  fish: Fish,
  mushrooms: Mushroom,
};

const PreferencesAvoidContainer = () => {
  const [preferences, setPreferences] = useState(null);
  const [allergyIds, setAllergyIds] = useState(null);
  const [dietaryPreferenceIds, setDietaryPreferenceIds] = useState(null);
  const { setProgress } = useContext(ProgressBarContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAllergyWarning, setShowAllergyWarning] = useState(false);
  const { brand } = useContext(BrandContext);
  const mealPlanSlug = useFunnelMealPlan();
  const { user, setUser } = useContext(UserContext);
  const navigate = useNavigate();
  const isFamilyMealPlan = mealPlanSlug === FAMILY;

  useMetadata({
    title: `Step 2: Preferences | ${brandData(brand)?.displayName}`,
  });

  useAnalyticsEffect(
    ({ events, logEvent }) => {
      if (brand) {
        logEvent(events.signUp[brand].preferencesAvoidances.viewed);
      }
    },
    [brand]
  );

  function handleVegetarianSelected(allergies, response, setPreferences) {
    const updatedAllergies = allergies.map(allergy =>
      ['Beef', 'Poultry', 'Fish', 'Pork', 'Shellfish'].includes(allergy.friendlyName)
        ? { ...allergy, selected: true }
        : allergy
    );
    setPreferences({
      ...response.preferences,
      allergies: updatedAllergies,
    });
  }

  function handlePescatarianSelected(allergies, response, setPreferences) {
    const updatedAllergies = allergies.map(allergy =>
      ['Beef', 'Poultry', 'Pork'].includes(allergy.friendlyName)
        ? { ...allergy, selected: true }
        : allergy
    );
    setPreferences({
      ...response.preferences,
      allergies: updatedAllergies,
    });
  }

  useEffect(() => {
    getPreferences().then(response => {
      if (response.ok) {
        const preferences = response.preferences.dietaryPreferences;
        const allergies = response.preferences.allergies;
        const isVegetarianSelected = preferences.some(
          preference => preference.friendlyName === VEGETARIAN && preference.selected
        );
        const isPescatarianSelected = preferences.some(
          preference => preference.friendlyName === PESCATARIAN && preference.selected
        );

        if (isVegetarianSelected) {
          handleVegetarianSelected(allergies, response, setPreferences);
        } else if (isPescatarianSelected) {
          handlePescatarianSelected(allergies, response, setPreferences);
        } else {
          setPreferences(response.preferences);
        }
      }
    });
  }, []);

  useEffect(() => {
    setProgress({ percent: '35' });

    createEvents({ params: { event_name: 'PageView' } }).catch(error =>
      ErrorLogging.captureException(error.message)
    );
  }, []);

  useEffect(() => {
    if (preferences?.allergies) {
      setAllergyIds(
        preferences.allergies
          .filter(allergy => allergy.selected === true)
          .map(allergy => allergy.id)
      );
    }
  }, [preferences?.allergies]);

  useEffect(() => {
    if (preferences?.dietaryPreferences) {
      setDietaryPreferenceIds(
        preferences.dietaryPreferences
          .filter(dietaryPreference => dietaryPreference.selected === true)
          .map(dietaryPreference => dietaryPreference.id)
      );
    }
  }, [preferences?.dietaryPreferences]);

  const handleAllergyClick = ({ friendlyName, selected }) => {
    const newAllergyState = preferences.allergies.map(allergy => {
      if (allergy.friendlyName === friendlyName) {
        return { ...allergy, selected: !selected };
      }
      return allergy;
    });

    setPreferences({ allergies: newAllergyState });
  };

  const handleOnClick = useAnalyticsCallback(
    (allergyIds, dietaryPreferenceIds) => {
      setIsSubmitting(true);
      updatePreferences({ params: { allergyIds, dietaryPreferenceIds } })
        .then(response => {
          setIsSubmitting(false);
          if (response.ok && response.preferences?.dietaryPreferences) {
            updateStatus({
              params: {
                status: isFamilyMealPlan ? Statuses.BOX_PEOPLE_STEP : Statuses.PROMO_STEP,
              },
            });
            setUser(prevUser => ({
              ...prevUser,
              status: isFamilyMealPlan ? Statuses.BOX_PEOPLE_STEP : Statuses.PROMO_STEP,
              dietaryPreferenceNames: response.preferences.dietaryPreferences
                .filter(pref => pref.selected === true)
                .map(pref => pref.friendlyName),
              optIns: response.optIns,
            }));
          }

          if (isFamilyMealPlan) {
            navigate(
              getBrandRoute({ brand: brand, isFamily: isFamilyMealPlan }).boxRecommenderPeople
            );
          } else {
            navigate(getBrandRoute({ brand: brand, isFamily: isFamilyMealPlan }).promo);
          }
        })
        .catch(error => {
          return error;
        });
    },
    ({ logEvent, events }) => {
      const allergies = preferences.allergies
        .filter(allergy => allergy.selected)
        .map(allergy => allergy.friendlyName);

      if (brand) {
        logEvent(events.signUp[brand].preferencesAvoidances.submitted, {
          allergies,
          mealPlanSlug: mealPlanSlug,
        });
      }
    },
    [preferences, brand, mealPlanSlug]
  );

  return (
    <>
      {user && preferences?.allergies ? (
        <>
          <div className={cn(styles[brand], styles.preferencesContainer, 'pb-4 pt-2')}>
            <div className="mb-6">
              <FunnelHeader
                title="What do you want to avoid?"
                titleClassName="text-secondary bp1:whitespace-nowrap"
                subtitle="You'll still have access to the full menu."
                subtitleClassName="h6 text"
              />
            </div>
            <section>
              <div className="grid w-full grid-cols-2 gap-x-1.5 gap-y-2">
                {preferences?.allergies.map(allergy => {
                  const IconComponent = IconMap[allergy.friendlyName.toLowerCase()];
                  return (
                    <ToggleButton
                      key={`column-${allergy.id}`}
                      id={allergy.id}
                      className="relative flex w-full items-center justify-between px-3"
                      name={allergy.name}
                      selected={allergy.selected}
                      handleClick={() => handleAllergyClick(allergy)}
                      icon={IconComponent}
                      type="refreshToggle"
                      showSelectedAfter
                      role="checkbox"
                      aria-checked={allergy.selected}
                      aria-label={allergy.friendlyName}
                    >
                      {allergy.friendlyName}
                    </ToggleButton>
                  );
                })}
              </div>
            </section>
            <Button
              type="submit"
              className="button mx-auto mt-8 w-full"
              onClick={() => handleOnClick(allergyIds, dietaryPreferenceIds)}
              isSubmitting={isSubmitting}
              variation="Pill"
            />
            <div className="mt-4 text-center">
              <Button
                type="button"
                onClick={() => setShowAllergyWarning(!showAllergyWarning)}
                className="inline-flex items-center gap-1 border-none bg-transparent p-0 text-charcoal-90 hover:text-charcoal-80"
                aria-expanded={showAllergyWarning}
                aria-controls="allergy-warning-text"
              >
                A note about serious food allergies
                <CaretDown
                  className={cn('iconSm transition-all', {
                    'rotate-180 transform': showAllergyWarning,
                  })}
                />
              </Button>
              <CSSTransition
                classNames={{
                  enter: 'fadeInOut-enter scaleHeight-enter',
                  enterActive: 'fadeInOut-enter-active scaleHeight-enter-active',
                  exit: 'fadeInOut-exit scaleHeight-exit',
                  exitActive: 'fadeInOut-exit-active scaleHeight-exit-active',
                }}
                in={showAllergyWarning}
                timeout={500}
                unmountOnExit
              >
                <div id="allergy-warning-text" className="mt-3">
                  <Text className="textSm mb-2 text-left text-charcoal-100">
                    All ingredients are individually packaged, but our central facility is not
                    certified allergen-free. Furthermore, ingredient contents may vary. Please check
                    ingredient packaging for allergens and nutrition facts.
                  </Text>
                  <Text className="textSm text-left text-charcoal-100">
                    If you have <strong>serious allergies</strong>, please use your best judgment or
                    consult a health professional to decide if our meals are safe for you!
                  </Text>
                </div>
              </CSSTransition>
            </div>
          </div>
          <MenuGallery />
        </>
      ) : (
        <PreferencesFormSkeleton />
      )}
    </>
  );
};

export default PreferencesAvoidContainer;
